/**
 * Settings → AI Plugins pane.
 *
 * Lists all configured plugins (loaded + not-loaded), lets the admin
 * add/remove/toggle them, edit per-plugin config + rules, and see cost
 * projection. Rendered inside SettingsDialog when tab==='plugins'.
 *
 * Design intent: this component is the ONLY place in EdgeWeb that knows
 * the admin workflow for runtime plugins. Everything else queries the
 * registry generically — add a new vendor and this page lists it
 * automatically.
 */

import { useEffect, useMemo, useState } from 'react';
import { loadRuntimePlugins } from '../plugins/runtime/registry';
import {
  loadAllowedPluginIds,
  loadPluginConfigs,
  savePluginConfigs,
} from '../plugins/runtime/settingsBridge';
import type {
  LoadedRuntimePlugin,
  RuntimePluginConfig,
  RuntimePluginRule,
} from '../plugins/runtime/types';
import { usePlugins } from '../plugins/runtime/hooks';
import { RULE_PRESETS } from '../plugins/runtime/rules/presets';
import { projectPluginCost, type PluginProjection } from '../plugins/runtime/rules/costProjection';

type View = 'list' | 'edit';

export default function PluginsSettingsPane() {
  const [view, setView] = useState<View>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [configs, setConfigs] = useState<RuntimePluginConfig[]>([]);
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const plugins = usePlugins();

  useEffect(() => {
    (async () => {
      const [c, a] = await Promise.all([
        loadPluginConfigs(true),
        loadAllowedPluginIds(true),
      ]);
      setConfigs(c);
      setAllowed(a);
      setLoading(false);
    })();
  }, []);

  async function persist(next: RuntimePluginConfig[]) {
    setConfigs(next);
    const ok = await savePluginConfigs(next);
    if (!ok) {
      setMessage('Save failed - check network');
      return;
    }
    await loadRuntimePlugins(next);
    setMessage('Saved');
    setTimeout(() => setMessage(null), 2000);
  }

  if (loading) return <div style={{ padding: 20 }}>Loading plugins…</div>;

  // Vendor hasn't licensed any plugins for this deployment.
  if (allowed.size === 0) {
    return (
      <div style={{ padding: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>AI Plugins</h3>
        <div style={{ marginTop: 16, padding: 12, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 13, color: '#4b5563' }}>
          No AI plugins have been enabled for this deployment. Contact your software vendor to add
          chest X-ray triage (LIRA), structured reporting hooks, or other plugin integrations.
        </div>
      </div>
    );
  }

  if (view === 'edit' && editingId) {
    const cfg = configs.find((c) => c.id === editingId);
    if (!cfg) {
      setView('list');
      return null;
    }
    return (
      <PluginEditor
        config={cfg}
        loaded={plugins.find((p) => p.manifest.id === editingId)}
        onBack={() => setView('list')}
        onSave={async (patch) => {
          const next = configs.map((c) => (c.id === editingId ? { ...c, ...patch } : c));
          await persist(next);
          setView('list');
        }}
        onDelete={async () => {
          if (!confirm(`Remove plugin ${cfg.displayName || cfg.id}?`)) return;
          await persist(configs.filter((c) => c.id !== editingId));
          setView('list');
        }}
      />
    );
  }

  return (
    <PluginList
      configs={configs}
      plugins={plugins}
      allowedIds={allowed}
      message={message}
      onEdit={(id) => { setEditingId(id); setView('edit'); }}
      onToggle={async (id, enabled) => {
        await persist(configs.map((c) => (c.id === id ? { ...c, enabled } : c)));
      }}
      onAdd={async (cfg) => {
        if (!allowed.has(cfg.id)) {
          setMessage(`Plugin id "${cfg.id}" is not licensed for this deployment. Contact your vendor.`);
          return;
        }
        if (configs.some((c) => c.id === cfg.id)) {
          setMessage(`Plugin id "${cfg.id}" already exists`);
          return;
        }
        await persist([...configs, cfg]);
        setEditingId(cfg.id);
        setView('edit');
      }}
    />
  );
}

// ---------- list view ----------

function PluginList({
  configs,
  plugins,
  allowedIds,
  message,
  onEdit,
  onToggle,
  onAdd,
}: {
  configs: RuntimePluginConfig[];
  plugins: LoadedRuntimePlugin[];
  allowedIds: Set<string>;
  message: string | null;
  onEdit: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => Promise<void>;
  onAdd: (cfg: RuntimePluginConfig) => Promise<void>;
}) {
  const [showAdd, setShowAdd] = useState(false);
  return (
    <div style={{ padding: 20 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>AI Plugins</h3>
      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
        Vendor-provided services like LIRA (chest X-ray triage) or on-prem utility
        plugins. Each entry points at a sidecar process; EdgeWeb fetches its
        /manifest at boot to learn capabilities.
      </p>
      {message && (
        <div style={{ padding: 8, background: '#dcfce7', color: '#166534', fontSize: 12, borderRadius: 4, marginBottom: 10 }}>
          {message}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        {configs.length === 0 && (
          <div style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>
            No plugins installed yet.
          </div>
        )}
        {configs.map((c) => {
          const loaded = plugins.find((p) => p.manifest.id === c.id);
          return (
            <PluginRow
              key={c.id}
              config={c}
              loaded={loaded}
              onEdit={() => onEdit(c.id)}
              onToggle={(e) => onToggle(c.id, e)}
            />
          );
        })}
      </div>
      <div style={{ marginTop: 16 }}>
        <button onClick={() => setShowAdd(true)}>+ Add plugin</button>
        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>
          Licensed plugins: {[...allowedIds].join(', ')}
        </span>
      </div>
      {showAdd && (
        <AddPluginDialog
          onCancel={() => setShowAdd(false)}
          onAdd={async (cfg) => {
            await onAdd(cfg);
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
}

function PluginRow({
  config,
  loaded,
  onEdit,
  onToggle,
}: {
  config: RuntimePluginConfig;
  loaded?: LoadedRuntimePlugin;
  onEdit: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const name = config.displayName || loaded?.manifest.name || config.id;
  const regClass = loaded?.manifest.vendor.regulatoryClass;
  const desc = loaded?.manifest.description;
  const err = loaded && !loaded.loaded ? loaded.loadError : null;

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 12,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <input
        type="checkbox"
        checked={config.enabled}
        onChange={(e) => onToggle(e.target.checked)}
        style={{ marginTop: 2 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{name}</span>
          {regClass && (
            <span style={{ fontSize: 10, color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 3, padding: '0 4px' }}>
              {regClass}
            </span>
          )}
          <span style={{ fontSize: 10, color: '#6b7280' }}>
            {loaded?.manifest.category ?? 'unknown'}
          </span>
        </div>
        {desc && <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4 }}>{desc}</div>}
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
          {config.baseUrl}
          {loaded?.loaded && config.perCallCost != null && (
            <> · ₹{config.perCallCost}/call</>
          )}
        </div>
        {err && (
          <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
            {err}
          </div>
        )}
      </div>
      <button onClick={onEdit}>Configure</button>
    </div>
  );
}

// ---------- add dialog ----------

function AddPluginDialog({
  onCancel,
  onAdd,
}: {
  onCancel: () => void;
  onAdd: (cfg: RuntimePluginConfig) => Promise<void>;
}) {
  const [baseUrl, setBaseUrl] = useState('https://localhost:8003');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div style={backdropStyle} onClick={onCancel}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <h4 style={{ margin: 0 }}>Add plugin</h4>
        <p style={{ fontSize: 12, color: '#6b7280' }}>
          Enter the plugin sidecar's base URL. EdgeWeb will fetch /manifest to
          read the plugin id and capabilities.
        </p>
        <input
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          style={{ width: '100%', padding: 6 }}
        />
        {error && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const resp = await fetch(`${baseUrl.replace(/\/+$/, '')}/manifest`);
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const manifest = await resp.json();
                if (!manifest.id) throw new Error('Manifest has no id');
                await onAdd({
                  id: manifest.id,
                  displayName: manifest.name,
                  baseUrl: baseUrl.replace(/\/+$/, ''),
                  enabled: true,
                  config: manifest.defaultConfig ?? {},
                  rules: [],
                });
              } catch (e) {
                setError(`Could not reach the plugin: ${e instanceof Error ? e.message : String(e)}`);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Testing…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- edit view ----------

function PluginEditor({
  config,
  loaded,
  onBack,
  onSave,
  onDelete,
}: {
  config: RuntimePluginConfig;
  loaded?: LoadedRuntimePlugin;
  onBack: () => void;
  onSave: (patch: Partial<RuntimePluginConfig>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [patch, setPatch] = useState<Partial<RuntimePluginConfig>>({});
  const current: RuntimePluginConfig = { ...config, ...patch };
  const schema = loaded?.manifest.configSchema as any;

  const [projection, setProjection] = useState<PluginProjection | null>(null);
  useEffect(() => {
    const rules = current.rules ?? [];
    if (rules.length === 0) {
      setProjection(null);
      return;
    }
    (async () => {
      const p = await projectPluginCost(
        rules as any,
        current.perCallCost ?? 25,
        current.monthlyBudget,
      );
      setProjection(p);
    })();
  }, [JSON.stringify(current.rules), current.perCallCost, current.monthlyBudget]);

  return (
    <div style={{ padding: 20, overflowY: 'auto', maxHeight: 'calc(100vh - 150px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onBack}>← Back</button>
        <h3 style={{ margin: 0, flex: 1 }}>{current.displayName || config.id}</h3>
        <button onClick={onDelete} style={{ color: '#dc2626' }}>Delete</button>
      </div>

      {loaded && !loaded.loaded && (
        <div style={{ marginTop: 12, padding: 10, background: '#fef2f2', color: '#991b1b', fontSize: 12 }}>
          Plugin unreachable: {loaded.loadError}
        </div>
      )}

      {/* Credentials live server-side in the sidecar's nssm env vars (not
          in the browser-visible config). This section just reports whether
          the connection works — rotation is a server-admin action. */}
      <section style={sectionStyle}>
        <h4 style={h4Style}>Credentials</h4>
        <ServerCredentialStatus plugin={loaded} />
      </section>

      {/* Base URL */}
      <section style={sectionStyle}>
        <h4 style={h4Style}>Connection</h4>
        <label style={labelStyle}>Base URL</label>
        <input
          value={current.baseUrl}
          onChange={(e) => setPatch({ ...patch, baseUrl: e.target.value })}
          style={inputStyle}
        />
      </section>

      {/* Config form (from schema) */}
      {schema?.properties && (
        <section style={sectionStyle}>
          <h4 style={h4Style}>Plugin config</h4>
          {Object.entries(schema.properties as Record<string, any>).map(([key, prop]) => (
            <ConfigField
              key={key}
              name={key}
              prop={prop}
              value={(current.config as any)[key]}
              onChange={(v) => setPatch({ ...patch, config: { ...current.config, [key]: v } })}
            />
          ))}
        </section>
      )}

      {/* Cost */}
      <section style={sectionStyle}>
        <h4 style={h4Style}>Cost</h4>
        <label style={labelStyle}>Per-call cost (₹)</label>
        <input
          type="number"
          value={current.perCallCost ?? ''}
          placeholder="25"
          onChange={(e) => setPatch({ ...patch, perCallCost: e.target.value === '' ? undefined : Number(e.target.value) })}
          style={inputStyle}
        />
        <label style={labelStyle}>Monthly budget soft cap (₹) — optional</label>
        <input
          type="number"
          value={current.monthlyBudget ?? ''}
          placeholder="5000"
          onChange={(e) => setPatch({ ...patch, monthlyBudget: e.target.value === '' ? undefined : Number(e.target.value) })}
          style={inputStyle}
        />
      </section>

      {/* Compliance strings — mandated verbatim text. Shown so admins
          can see what their readers will see on the plugin's result cards
          and opt-in surfaces, and so any vendor contract change can be
          verified at a glance. */}
      {loaded?.manifest.complianceStrings && loaded.manifest.complianceStrings.length > 0 && (
        <section style={sectionStyle}>
          <h4 style={h4Style}>Compliance (verbatim strings)</h4>
          <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 10px' }}>
            Text the vendor requires EdgeWeb to render as-is. Modification without the vendor's
            written approval is outside their validated scope of use.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loaded.manifest.complianceStrings.map((cs, i) => (
              <div
                key={i}
                style={{
                  padding: 8,
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <div style={{ fontSize: 10, color: '#6b7280' }}>
                  {cs.slot}
                  {cs.source && <> · {cs.source}</>}
                </div>
                <div style={{ marginTop: 4 }}>{cs.text}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Rules */}
      <section style={sectionStyle}>
        <h4 style={h4Style}>Trigger rules</h4>
        <RuleEditor
          rules={current.rules ?? []}
          onChange={(rules) => setPatch({ ...patch, rules })}
        />
      </section>

      {/* Projection */}
      {projection && (
        <section style={sectionStyle}>
          <h4 style={h4Style}>Cost projection</h4>
          <div style={{ fontSize: 12 }}>
            Last 7 days matched by any SEND rule: <strong>{projection.totalMatchCount7d}</strong> studies<br />
            Projected monthly spend: <strong>₹{projection.totalEstimatedMonthlyCost.toFixed(0)}</strong>
            {projection.monthlyBudget != null && (
              <> &nbsp;/ budget ₹{projection.monthlyBudget}</>
            )}
          </div>
          {projection.overBudget && (
            <div style={{ marginTop: 6, padding: 6, background: '#fef3c7', color: '#92400e', fontSize: 12 }}>
              ⚠ Projection exceeds your monthly budget cap — raise the cap or move more rules to manual.
            </div>
          )}
        </section>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onBack}>Cancel</button>
        <button
          onClick={async () => {
            await onSave(patch);
          }}
          disabled={Object.keys(patch).length === 0}
        >
          Save changes
        </button>
      </div>
    </div>
  );
}

function ConfigField({
  name,
  prop,
  value,
  onChange,
}: {
  name: string;
  prop: any;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = prop.title || name;
  const type = prop.type;
  const isPassword = prop.format === 'password';
  if (type === 'boolean') {
    return (
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
    );
  }
  if (type === 'number') {
    return (
      <div style={{ margin: '8px 0' }}>
        <label style={labelStyle}>{label}</label>
        <input
          type="number"
          value={(value as number) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          style={inputStyle}
        />
      </div>
    );
  }
  // Default: string / password
  return (
    <div style={{ margin: '8px 0' }}>
      <label style={labelStyle}>{label}</label>
      <input
        type={isPassword ? 'password' : 'text'}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        autoComplete={isPassword ? 'new-password' : undefined}
      />
    </div>
  );
}

// ---------- rule editor ----------

function RuleEditor({
  rules,
  onChange,
}: {
  rules: RuntimePluginRule[];
  onChange: (rules: RuntimePluginRule[]) => void;
}) {
  function applyPreset(id: string) {
    const preset = RULE_PRESETS.find((p) => p.id === id);
    if (preset) onChange(preset.rules as RuntimePluginRule[]);
  }
  const sorted = useMemo(() => [...rules].sort((a, b) => a.priority - b.priority), [rules]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <label style={{ fontSize: 12 }}>Load preset:</label>
        <select onChange={(e) => { if (e.target.value) applyPreset(e.target.value); e.target.value=''; }}>
          <option value="">(choose preset)</option>
          {RULE_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      {sorted.length === 0 && (
        <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
          No rules configured — plugin fires only on manual bulk-send.
        </div>
      )}
      {sorted.map((rule, idx) => (
        <RuleRow
          key={`${rule.priority}-${rule.name}-${idx}`}
          rule={rule}
          onChange={(next) => {
            const copy = [...rules];
            copy[idx] = next;
            onChange(copy);
          }}
          onDelete={() => onChange(rules.filter((_, i) => i !== idx))}
        />
      ))}
      <button
        onClick={() =>
          onChange([
            ...rules,
            {
              priority: Math.max(0, ...rules.map((r) => r.priority)) + 10,
              enabled: true,
              name: 'New rule',
              when: 'on_worklist_view',
              match: {},
              action: 'send',
            },
          ])
        }
      >
        + Add rule
      </button>
    </div>
  );
}

function RuleRow({
  rule,
  onChange,
  onDelete,
}: {
  rule: RuntimePluginRule;
  onChange: (r: RuntimePluginRule) => void;
  onDelete: () => void;
}) {
  const matchJson = JSON.stringify(rule.match ?? {}, null, 2);
  const [matchDraft, setMatchDraft] = useState(matchJson);
  useEffect(() => setMatchDraft(JSON.stringify(rule.match ?? {}, null, 2)), [JSON.stringify(rule.match)]);

  function commitMatch(text: string) {
    try {
      onChange({ ...rule, match: JSON.parse(text) });
    } catch { /* leave the draft as-is until valid JSON */ }
  }
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: 10, marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" checked={rule.enabled} onChange={(e) => onChange({ ...rule, enabled: e.target.checked })} />
        <input
          value={rule.name}
          onChange={(e) => onChange({ ...rule, name: e.target.value })}
          style={{ flex: 1, padding: 4 }}
        />
        <input
          type="number"
          value={rule.priority}
          onChange={(e) => onChange({ ...rule, priority: Number(e.target.value) })}
          style={{ width: 60, padding: 4 }}
          title="priority (lower = evaluated first)"
        />
        <select
          value={rule.when}
          onChange={(e) => onChange({ ...rule, when: e.target.value as any })}
        >
          <option value="on_study_arrive">on_study_arrive</option>
          <option value="on_worklist_view">on_worklist_view</option>
          <option value="on_study_open">on_study_open</option>
          <option value="on_report_signed">on_report_signed</option>
          <option value="manual">manual</option>
        </select>
        <select value={rule.action} onChange={(e) => onChange({ ...rule, action: e.target.value as any })}>
          <option value="send">send</option>
          <option value="skip">skip</option>
        </select>
        <button onClick={onDelete} style={{ color: '#dc2626' }}>×</button>
      </div>
      <div style={{ marginTop: 6 }}>
        <label style={{ fontSize: 11, color: '#6b7280' }}>Match (JSON)</label>
        <textarea
          value={matchDraft}
          onChange={(e) => setMatchDraft(e.target.value)}
          onBlur={() => commitMatch(matchDraft)}
          rows={4}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, padding: 4 }}
        />
      </div>
    </div>
  );
}

/** Shows whether the sidecar's own credentials (nssm env vars) are
 *  configured and whether the vendor API accepts them. The browser
 *  never sees the raw credentials - just a traffic-light status. */
function ServerCredentialStatus({ plugin }: { plugin?: LoadedRuntimePlugin }) {
  const [state, setState] = useState<'checking' | 'ok' | 'misconfigured' | 'unreachable'>('checking');
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    if (!plugin || !plugin.loaded) {
      setState('unreachable');
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`${plugin.baseUrl}/health`);
        if (!resp.ok) { setState('unreachable'); return; }
        const h = await resp.json();
        if (h.credentials_configured === false) {
          setState('misconfigured');
          setDetail('Credentials not set in sidecar environment. SSH to server and run `nssm edit <service>` to configure.');
          return;
        }
        // Try quota - this proves the credentials actually work against the vendor.
        try {
          const qResp = await fetch(`${plugin.baseUrl}/triage/quota`);
          if (!qResp.ok) {
            setState('misconfigured');
            setDetail(`Vendor rejected credentials: HTTP ${qResp.status}`);
            return;
          }
          setState('ok');
        } catch {
          setState('ok');
          setDetail('(Sidecar healthy; quota check skipped)');
        }
      } catch {
        setState('unreachable');
      }
    })();
  }, [plugin?.manifest.id]);

  const label = {
    checking: '…checking',
    ok: '✓ Configured server-side',
    misconfigured: '✗ Not configured',
    unreachable: '✗ Sidecar unreachable',
  }[state];
  const color = state === 'ok' ? '#16a34a' : state === 'checking' ? '#6b7280' : '#dc2626';
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ color, fontWeight: 600 }}>{label}</div>
      {detail && <div style={{ color: '#4b5563', marginTop: 4 }}>{detail}</div>}
      <div style={{ color: '#6b7280', marginTop: 8, fontSize: 11 }}>
        Credentials are stored in the sidecar process environment (nssm) and never cross to
        the browser. To rotate: SSH to the server, update the service's AppEnvironmentExtra,
        and restart the sidecar.
      </div>
    </div>
  );
}

// ---------- shared styles ----------

const sectionStyle = { marginTop: 20, padding: 12, border: '1px solid #e5e7eb', borderRadius: 4 };
const h4Style = { margin: '0 0 10px', fontSize: 13 };
const labelStyle = { display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 2 };
const inputStyle = { width: '100%', padding: 6, boxSizing: 'border-box' as const };
const backdropStyle = {
  position: 'fixed' as const,
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
};
const dialogStyle = {
  background: '#fff',
  padding: 20,
  borderRadius: 6,
  minWidth: 400,
};
